import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail, fileUrl } from "../lib/api";
import { PageHeader, Empty, Modal, Chip, EmbeddedActionBar } from "../components/ui-bits";
import { Plus, Search, Edit3, Archive, ArchiveRestore, Upload, X } from "lucide-react";
import { useBrandPermissions } from "../lib/brand";
import { useT } from "../lib/i18n";
import { useAutoOpenFromQuery } from "../lib/useAutoOpenFromQuery";

const SEASONS = ["SS", "FW", "Resort", "Pre-Fall", "Continuativa", "Altro"];
const STATUSES = ["Bozza", "In sviluppo", "Campionario", "Produzione", "Completata", "Archiviata"];

const STATUS_STYLE = {
  "Bozza":       { bg: "rgba(115,115,115,.16)", fg: "var(--text-primary)" },
  "In sviluppo": { bg: "rgba(37,99,235,.16)",   fg: "#1D4ED8" },
  "Campionario": { bg: "rgba(217,119,6,.18)",   fg: "#A45A05" },
  "Produzione":  { bg: "rgba(15,133,84,.16)",   fg: "#0F8554" },
  "Completata":  { bg: "rgba(15,118,110,.18)",  fg: "#0F766E" },
  "Archiviata":  { bg: "rgba(115,115,115,.14)", fg: "var(--text-secondary)" },
};
function StatusChip({ value }) {
  const s = STATUS_STYLE[value] || STATUS_STYLE["Bozza"];
  return (
    <span className="chip" style={{ background: s.bg, color: s.fg, borderColor: s.bg }}>
      <span className="w-1.5 h-1.5 rounded-full inline-block mr-1.5" style={{ background: s.fg }}/>
      {value || "—"}
    </span>
  );
}

const emptyForm = () => ({
  name: "", season: "", year: new Date().getFullYear(),
  description: "", status: "Bozza", cover_image_id: null,
});

export default function CollectionsPage({ embedded = false } = {}) {
  const { canEdit, canAdmin } = useBrandPermissions();
  const t = useT();
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => {
    const params = { search: search || undefined };
    if (status) params.status = status;
    if (includeArchived) params.include_archived = true;
    return api.get("/collections", { params }).then(r => setList(r.data));
  };
  useEffect(() => { load(); api.get("/clients").then(r => setClients(r.data)); }, [search, status, includeArchived]);

  const clientName = (id) => {
    const c = clients.find(x => x.id === id);
    return c ? (c.name || c.brand || "—") : "—";
  };

  const open = (row) => {
    if (row) { setForm({ ...emptyForm(), ...row }); setEditId(row.id); }
    else { setForm(emptyForm()); setEditId(null); }
    setError(""); setModal(true);
  };
  useAutoOpenFromQuery(list, open);
  const save = async (e) => {
    e.preventDefault(); setError("");
    try {
      const payload = { ...form, year: form.year ? Number(form.year) : null };
      if (editId) await api.put(`/collections/${editId}`, payload);
      else {
        const r = await api.post("/collections", payload);
        setEditId(r.data.id);
      }
      setModal(false); load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const archive = async (row) => {
    if (!window.confirm(t("coll.confirm.archive"))) return;
    await api.post(`/collections/${row.id}/archive`); load();
  };
  const unarchive = async (row) => { await api.post(`/collections/${row.id}/unarchive`); load(); };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    // We need an id to link the file to. If new collection, save first.
    let cid = editId;
    if (!cid) {
      // create a minimal draft to obtain an id
      try {
        const r = await api.post("/collections", { ...form, year: form.year ? Number(form.year) : null });
        cid = r.data.id; setEditId(cid);
      } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); return; }
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("linked_type", "collection_cover");
      fd.append("linked_id", cid);
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
      const patched = { ...form, cover_image_id: r.data.id };
      await api.put(`/collections/${cid}`, { ...patched, year: patched.year ? Number(patched.year) : null });
      setForm(patched); load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div data-testid="collections-page">
      {!embedded && (
      <PageHeader eyebrow="Seasonal Lines" title="Collections"
        action={canEdit && (
          <button data-testid="new-collection-btn" onClick={()=>open(null)} className="btn-gold inline-flex items-center gap-2"><Plus size={14}/>New</button>
        )}/>
      )}

      {embedded && canEdit && (
        <EmbeddedActionBar action={
          <button data-testid="new-collection-btn" onClick={()=>open(null)} className="btn-gold inline-flex items-center gap-2">
            <Plus size={14}/> {t("collections.new")}
          </button>
        }/>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted,#737373)]"/>
          <input data-testid="collections-search" placeholder={t("common.search")} value={search} onChange={(e)=>setSearch(e.target.value)} className="input-dark pl-9"/>
        </div>
        <select data-testid="collections-filter-status" className="input-dark max-w-xs" value={status} onChange={(e)=>setStatus(e.target.value)}>
          <option value="">{t("coll.filter.all_status")}</option>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 text-xs">
          <input type="checkbox" data-testid="collections-include-archived" checked={includeArchived} onChange={(e)=>setIncludeArchived(e.target.checked)}/>
          {t("coll.filter.include_archived")}
        </label>
      </div>

      {list.length === 0 ? (
        <Empty
          title={t("collections.empty")}
          cta={canEdit ? { label: t("collections.cta_first_coll"), onClick: ()=>open(null) } : null}
          ctaTestId="collections-empty-cta"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map(c => (
            <div key={c.id} className="card-dark overflow-hidden hover-lift" data-testid={`collection-card-${c.id}`}>
              <div className="aspect-[16/10] bg-[color:var(--surface,#F5F5F5)] relative overflow-hidden">
                {c.cover_image_id ? (
                  <img src={fileUrl(c.cover_image_id)} alt={c.name} className="w-full h-full object-cover"/>
                ) : (
                  <div className="w-full h-full flex items-center justify-center label-caps text-[color:var(--text-muted,#737373)]">
                    {c.season || "—"} · {c.year || "—"}
                  </div>
                )}
                <div className="absolute top-3 left-3"><StatusChip value={c.status}/></div>
                {canEdit && (
                  <div className="absolute top-3 right-3 flex gap-1">
                    <button onClick={()=>open(c)} className="bg-white/85 hover:bg-white p-1.5 rounded-sm text-[#0A0A0A]" data-testid={`coll-edit-${c.id}`}><Edit3 size={12}/></button>
                    {c.archived ? (
                      <button onClick={()=>unarchive(c)} className="bg-white/85 hover:bg-white p-1.5 rounded-sm text-[#0A0A0A]" data-testid={`coll-unarchive-${c.id}`}><ArchiveRestore size={12}/></button>
                    ) : (
                      <button onClick={()=>archive(c)} className="bg-white/85 hover:bg-white p-1.5 rounded-sm text-[#0A0A0A]" data-testid={`coll-archive-${c.id}`}><Archive size={12}/></button>
                    )}
                  </div>
                )}
              </div>
              <Link to={`/collections/${c.id}`} data-testid={`collection-open-${c.id}`} className="block p-5">
                <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted,#737373)] mb-1">{c.season || "—"} · {c.year || "—"}</div>
                <h3 className="font-serif text-2xl leading-tight">{c.name}</h3>
                {c.description && <p className="text-xs text-[color:var(--text-muted,#737373)] mt-2 line-clamp-2">{c.description}</p>}

                {/* Progress + garments count */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[color:var(--text-muted,#737373)] mb-1">
                    <span>{c.garments_count || 0} {t("coll.card.garments")}</span>
                    <span>{Math.round((c.progress || 0) * 100)}%</span>
                  </div>
                  <div className="h-1 bg-[color:var(--surface,#F5F5F5)]" data-testid={`coll-progress-${c.id}`}>
                    <div className="h-full bg-[color:var(--text-primary,#0A0A0A)] transition-all" style={{ width: `${Math.round((c.progress || 0) * 100)}%` }}/>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={()=>setModal(false)} title={editId ? t("coll.modal.edit") : t("coll.modal.new")} wide>
        <form onSubmit={save} className="space-y-4">
          {/* Cover image */}
          <div>
            <label className="label-caps block mb-2">{t("coll.field.cover")}</label>
            <div className="flex items-center gap-3">
              {form.cover_image_id ? (
                <div className="relative">
                  <img src={fileUrl(form.cover_image_id)} className="w-24 h-16 object-cover border border-[color:var(--border,#E5E5E5)]" alt=""/>
                  <button type="button" onClick={()=>setForm({...form, cover_image_id: null})} className="absolute -top-1 -right-1 bg-black/70 text-white rounded-full p-0.5" data-testid="coll-cover-remove"><X size={10}/></button>
                </div>
              ) : (
                <div className="w-24 h-16 border border-dashed border-[color:var(--border,#E5E5E5)] flex items-center justify-center text-[color:var(--text-muted,#737373)]"><Upload size={16}/></div>
              )}
              <label className="btn-ghost inline-flex items-center gap-2 cursor-pointer" data-testid="coll-cover-upload">
                <Upload size={14}/> {uploading ? "…" : "Carica"}
                <input type="file" accept="image/*" hidden onChange={uploadCover}/>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t("coll.field.name")} required>
              <input data-testid="collection-form-name" required className="input-dark" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})}/>
            </Field>
            <Field label={t("coll.field.season")}>
              <select data-testid="collection-form-season" className="input-dark" value={form.season||""} onChange={(e)=>setForm({...form, season: e.target.value})}>
                <option value="">—</option>{SEASONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <Field label={t("coll.field.year")}>
              <input data-testid="collection-form-year" type="number" className="input-dark" value={form.year||""} onChange={(e)=>setForm({...form, year: e.target.value})}/>
            </Field>
            <Field label={t("coll.field.status")}>
              <select data-testid="collection-form-status" className="input-dark" value={form.status} onChange={(e)=>setForm({...form, status: e.target.value})}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label={t("coll.field.description")}>
                <textarea rows={3} className="input-dark" value={form.description||""} onChange={(e)=>setForm({...form, description: e.target.value})}/>
              </Field>
            </div>
          </div>
          {error && <div className="text-red-600 text-sm" data-testid="coll-form-error">{error}</div>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={()=>setModal(false)} className="btn-ghost">{t("coll.modal.cancel")}</button>
            <button data-testid="collection-form-save" className="btn-gold">{t("coll.modal.save")}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="label-caps block mb-2">{label}{required && <span className="text-[color:var(--accent,#D4AF37)] ml-1">*</span>}</label>
      {children}
    </div>
  );
}
